<template>
  <div>
    <div class="d-flex button-group"
         :class="{'justify-content-end': isNewInvoice, 'justify-content-between': !isNewInvoice}">
      <b-button class="history-btn" v-if="!isNewInvoice" @click="openHistoryModal">
        {{$t('buttons.common.history')}}
      </b-button>
      <history-modal-content></history-modal-content>
      <div class="button-group margin-0 d-flex" v-if="currentRoutePermissions.change_purchaseinvoice">
        <template v-if="!isNewInvoice">

          <b-btn variant="success" size="sm" @click="changeInvoiceStatus('move_status_forward')"
                 v-show="isDraft">
            {{$t('buttons.common.confirm')}}
          </b-btn>
          <b-btn size="sm" @click="changeInvoiceStatus('move_status_back')"
                 v-show="invoiceData.status === 'approved'">
            {{$t('buttons.common.roll_back')}}
          </b-btn>
        </template>
        <b-btn variant="outline-success" size="sm" @click="save">
          {{$t('buttons.common.save')}}
        </b-btn>
      </div>
    </div>
    <div class="d-flex justify-content-between toggle-container" @click="showCollapse = !showCollapse"
         :aria-expanded="showCollapse"
         aria-controls="collapse">
      <h3>
        {{invoiceLabel}}
      </h3>
      <div class="icons-wrapper">
        {{invoiceInfo}}
        <icon name="angle-down" class="angle" v-if="showCollapse"></icon>
        <icon name="angle-right" class="angle" v-else></icon>
      </div>
    </div>
    <div>
      <b-collapse id="showCollapse" v-model="showCollapse">
        <div class="filter-form">
          <div>
            <label>{{$t('labels.invoices.invoice_number')}}</label>
            <input class="form-control" v-model="invoiceData.invoice_number" placeholder=""
                   :class="{ required: !invoiceData.invoice_number && isLoadedData}"
                   :disabled="isApprovedInvoice">
          </div>
          <div>
            <label>{{$t('labels.common.supplier')}}</label>
            <multiselect v-model="invoiceData.supplier" :max-height="200"
                         placeholder="" label="name" track-by="name"
                         :options="suppliers" :close-on-select="true"
                         :class="{ required: !invoiceData.supplier && isLoadedData}"
                         :disabled="isApprovedInvoice"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div>
            <label>{{$t('labels.common.currency')}}</label>
            <multiselect v-model="invoiceData.currency" :max-height="200"
                         placeholder="" label="code" track-by="code"
                         :options="currencies" :close-on-select="true"
                         :class="{ required: !invoiceData.currency && isLoadedData}"
                         @input="fetchCurrencyRate" :allow-empty="false"
                         :disabled="isApprovedInvoice"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div>
            <label>{{$t('labels.contracts.contract_number')}}</label>
            <multiselect v-model="invoiceData.contract" :max-height="200"
                         placeholder="" label="contract_nr" track-by="contract_nr"
                         :options="purchaseContracts" close-on-select disabled
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div>
            <label>{{$t('labels.invoices.invoice_date')}}</label>
            <datepicker v-model="invoiceData.invoice_date" lang="en" :first-day-of-week="1"
                        placeholder=""
                        :class="{ required: !invoiceData.invoice_date && isLoadedData}"
                        :not-after="new Date()" :clearable="false" :disabled="isApprovedInvoice"
                        @change="fetchCurrencyRate"></datepicker>
          </div>
          <div>
            <label>{{$t('labels.common.buyer')}}</label>
            <multiselect v-model="invoiceData.buyer" :max-height="200"
                         placeholder="" label="name" track-by="name"
                         :options="buyers" :close-on-select="true"
                         :disabled="isApprovedInvoice"
                         :class="{ required: !invoiceData.buyer && isLoadedData}"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div>
            <label>{{$t('labels.agents.invoice_sum')}}</label>
            <float-input v-model="invoiceData.total" placeholder=""
                         :class="{ required: invoiceData.total === '' && isLoadedData}"
                         :disabled="isApprovedInvoice" :input="convertSumToBaseCurr" :only-positive="true">
            </float-input>
          </div>
          <div>
            <label>{{$t('labels.invoices.difference')}} ({{base_currency.symbol}})</label>
            <float-input v-model="invoiceData.invoice_sums_difference" placeholder=""
                         :disabled="!isSuperuserOrAppraiser">
            </float-input>
          </div>
<!--          <div>-->
<!--            <label>{{$t('labels.common.additional_sum')}} ({{base_currency.symbol}})</label>-->
<!--            <float-input v-model="invoiceData.additional_sum" placeholder=""-->
<!--                         :disabled="!isSuperuserOrAppraiser">-->
<!--            </float-input>-->
<!--          </div>-->
          <div>
            <label>{{$t('labels.common.receiving_date')}}</label>
            <datepicker v-model="invoiceData.acceptance_date" lang="en" :first-day-of-week="1"
                        placeholder=""
                        :disabled="isApprovedInvoice"></datepicker>
          </div>
          <div class="checkbox-wrapper">
            <label for="prepaid">{{$t('labels.invoices.prepayment')}}</label>
            <input id="prepaid" type="checkbox" v-model="invoiceData.prepaid" :disabled="isApprovedInvoice">
          </div>
          <div>
            <label>{{$t('labels.agents.invoice_sum')}} ({{base_currency.symbol}})</label>
            <float-input v-model="invoiceData.total_base_curr" :disabled="true"></float-input>
            <div class="rate" v-if="invoiceData.invoice_date && invoiceData.currency">
              {{$t('common_content.currency_rate_on_date',
              {course: `${base_currency.code}/${invoiceData.currency.code}`, rateDate})}} {{rate.toFixed(5)}}
            </div>
          </div>
          <div>
            <label>{{$t('labels.common.percent')}}, %</label>
            <float-input v-model="invoiceData.percent_difference" placeholder=""> </float-input>
          </div>
          <div>
            <label>{{$t('labels.common.seller')}}</label>
            <multiselect v-model="invoiceData.seller" :max-height="200"
                         placeholder="" label="name" track-by="name"
                         :options="sellers" :close-on-select="true"
                         :class="{ required: !invoiceData.seller && isLoadedData}"
                         :disabled="isApprovedInvoice"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div>
            <label>{{$t('labels.common.due_date')}}</label>
            <datepicker v-model="invoiceData.date_due" lang="en" :first-day-of-week="1"
                        placeholder=""
                        :disabled="invoiceData.prepaid || isApprovedInvoice"></datepicker>
          </div>
          <div>
            <label>{{$t('labels.common.actual_evaluation')}} ({{base_currency.symbol}})</label>
            <float-input v-model="invoiceData.actual_evaluation_cost" placeholder="" disabled>
            </float-input>
          </div>
          <div>
            <label>{{$t('labels.common.caching_price')}}</label>
            <float-input :value="invoiceData.caching_price > 0 ? invoiceData.caching_price : ''" placeholder="" disabled></float-input>
          </div>
          <div></div>
          <div></div>
          <div></div>
          <div>
            <label>{{$t('labels.invoices.fact_eval_sum')}} ({{base_currency.symbol}})</label>
            <float-input v-model="invoiceData.actual_cost" placeholder=""
                         disabled style="background-color: #f1c2c2">
            </float-input>
          </div>
          <div></div>
          <div></div>
          <div></div>
          <div>
            <label>{{$t('labels.common.notes')}}</label>
            <b-form-textarea v-model="invoiceData.notes" placeholder=""></b-form-textarea>
          </div>
        </div>
      </b-collapse>
    </div>
    <div v-if="isLoadedData && !isNewInvoice">
      <b-card no-body>
        <b-tabs card v-model="currentTabIndex">
<!--          <b-tab @click="setGridData(acceptancesGrid, acceptances)">-->
<!--            <template slot="title">-->
<!--              <portal-target name="receivings_tab"></portal-target>-->
<!--            </template>-->
<!--            <portal to="receivings_tab">{{$t('tabs.purchase_invoices.receivings')}}</portal>-->
<!--            <div class="actions-row" v-show="!isApprovedInvoice">-->
<!--              <div class="d-flex align-items-center">-->
<!--                <div>{{$t('labels.common.selected')}}: {{selectedBoundedAcceptances.length}}</div>-->
<!--              </div>-->
<!--            </div>-->
<!--            <ag-grid :gridOptions="acceptancesGrid" :style="gridHeight" class="ag-theme-balham grid"-->
<!--                     :columnDefs="acceptancesGridColumns"-->
<!--                     :rowData="acceptances" @selectionChanged="acceptancesSelectionChanged"-->
<!--                     :filterModified="acceptancesSelectionChanged"-->
<!--                     :pinnedBottomRowData="pinnedAcceptancesRowData"-->
<!--                     :postProcessPopup="postProcessPopup"-->
<!--            ></ag-grid>-->
<!--          </b-tab>-->
          <b-tab @click="setGridData(actualEvaluationGrid, factEvaluationData)">
            <template slot="title">
              {{$t('tabs.purchase_invoices.actual_evaluation')}}
            </template>
            <div class="actions-row">
              <div class="d-flex justify-content-end flex-grow-1 ml-1">
                <save-columns-btn :grid="actualEvaluationGrid" :grid-name="factEvaluationGridName" class="margin-left-5"></save-columns-btn>
              </div>
            </div>
            <!-- Факт. оценка | Additional cost -->
            <ag-grid :gridOptions="actualEvaluationGrid" :style="gridHeight" class="ag-theme-balham grid"
                     :postProcessPopup="postProcessPopup" :columnDefs="actualEvaluationColumns"
                     :rowData="factEvaluationData" @selectionChanged="actualEvaluationSelectionChanged"
                     @filterChanged="(event) => updateTotalData(event, 'actual')"
                     :pinnedBottomRowData="factEvaluationTotalData"
                     :filterModified="actualEvaluationSelectionChanged" :rowDataChanged="actualEvaluationSelectionChanged">
            </ag-grid>
          </b-tab>
          <b-tab @click="setGridData(additionalCostsGrid, additionalCosts)">
            <template slot="title">
              <portal-target name="additional_cost_tab"></portal-target>
            </template>
            <portal to="additional_cost_tab">
              {{$t('tabs.common.additional_cost')}}
            </portal>
            <ag-grid :gridOptions="additionalCostsGrid" :style="gridHeight" class="ag-theme-balham grid"
                     :columnDefs="additionalCostsGridColumns" :rowData="additionalCosts"
                     @filterChanged="(event) => updateTotalData(event, 'additional')"
                     :pinnedBottomRowData="pinnedAdditionalCostsRowData" :postProcessPopup="postProcessPopup">
            </ag-grid>
          </b-tab>
          <b-tab @click="setGridData(commissionFeesGrid, commissionFees)" v-if="isUserCanViewCommissionFees && isApprovedInvoice">
            <template slot="title">
              <portal-target name="commission_fees_tab"></portal-target>
            </template>
            <portal to="commission_fees_tab">
              {{$t('tabs.agents.commission_fees')}}
            </portal>
            <div class="button-group d-flex justify-content-end">
              <b-btn variant="success" @click="editCommissionFees">
                {{$t('buttons.common.add')}}
              </b-btn>
              <b-btn variant="danger" @click="deleteCommissionFees">
                {{$t('buttons.common.delete')}}
              </b-btn>
            </div>
            <purchase-agents-invoice-commission-fee-modal
              :saveCommissionFeeCb="saveCommissionFeeCb"
              :invoiceId="this.invoiceId"
              :invoiceData="invoiceDataForCommissionFeeModal"
            />
            <ag-grid :gridOptions="commissionFeesGrid" :style="gridHeight" class="ag-theme-balham grid"
                     :columnDefs="commissionFeesGridColumns" :rowData="commissionFees"
                     @rowDoubleClicked="editCommissionFees"
                     :pinnedBottomRowData="pinnedCommissionFeesRowData" :postProcessPopup="postProcessPopup">
            </ag-grid>
          </b-tab>
          <b-tab @click="handlePaymentsTabClick(paymentsGrid, payments)">
            <template slot="title">
              <portal-target name="payments_tab"></portal-target>
            </template>
            <portal to="payments_tab">
              {{$t('Payments')}}
            </portal>
            <ag-grid :gridOptions="paymentsGrid" :style="gridHeight" class="ag-theme-balham grid"
                     :columnDefs="paymentsGridColumns" :rowData="payments" :postProcessPopup="postProcessPopup">
            </ag-grid>
          </b-tab>
          <b-tab>
            <template slot="title">
              <portal-target name="documents_tab"></portal-target>
            </template>
            <portal to="documents_tab">
              {{$t('tabs.common.documents')}}
            </portal>
            <app-documents :isCollapse="showCollapse" :documents="documents" :url="`${commonUrl}attachments/`"
                           @loadDocuments="fetchDocuments"></app-documents>
          </b-tab>
          <b-tab>
            <template slot="title">
              <portal-target name="comments_tab"></portal-target>
            </template>
            <portal to="comments_tab">{{$t('tabs.common.comments')}}</portal>
            <app-comments :comments="comments" :url="`${commonUrl}comments/`"
                          @loadComments="fetchComments"></app-comments>
          </b-tab>
        </b-tabs>
      </b-card>
    </div>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import { mapState, mapGetters } from 'vuex';
  import Datepicker from 'vue2-datepicker';
  import Multiselect from 'vue-multiselect';
  import moment from 'moment';
  import {
    notificationsRenderer, formatNumber,
    LinkCellRenderer,
    autoResize,
    alignCells,
    getPinnedRowData,
    addGroupingInResponseData
  } from '@/helpers/renderHelpers';
  import {
    handlerResponse,
    setNotifyData,
    getFilteredSelectedNodes,
    addColIdToColumns, getArrayByProperty,
    convertTotalDataToObject,
    rightRound
  } from '@/helpers/commonHelpers';
  import exportExcel from '@/helpers/export-excel-helper';
  import { translateColumns } from '@/helpers/translate-helper';

  import Comments from '@/components/common/Comments';
  import Documents from '@/components/common/Documents';
  import HistoryModalContent from '@/components/modals/HistoryModal';
  import FloatInput from '@/components/common/FloatInput';
  import PurchaseAgentsInvoiceCommissionFeeModal from '@/components/modals/PurchaseAgentsInvoiceCommissionFeeModal';
  import ActualEvaluationModal from '@/components/modals/ActualEvaluationModal';
  import SaveColumnsBtn from '@/components/common/SaveColumnsBtn';
  import WdDefectPopover from '@/components/common/purchase-invoice/WdDefectPopover'
  import WdAdditionalPricePopover from '@/components/common/purchase-invoice/WdAdditionalPricePopover'

  import { PURCHASE_INVOICES, DATE_FORMAT } from '@/constants';
  import gridProps from '@/gridProps';
  import { gridMixins, updateTotalDataMixin } from '@/mixins';

  const actualEvalBaseUrl = 'woodware/movement_data/factual_evaluation/';

  export default {
    mixins: [gridMixins, updateTotalDataMixin],
    components: {
      Datepicker,
      Multiselect,
      'ag-grid': AgGridVue,
      HistoryModalContent,
      WdDefectPopover,
      WdAdditionalPricePopover,
      'app-comments': Comments,
      'app-documents': Documents,
      FloatInput,
      ActualEvaluationModal,
      PurchaseAgentsInvoiceCommissionFeeModal,
      SaveColumnsBtn
    },
    data() {
      return {
        isLoadedData: false,
        showCollapse: true,
        invoiceData: {
          invoice_number: '',
          supplier: null,
          currency: null,
          contract: null,
          invoice_date: null,
          buyer: null,
          total: '',
          additional_total: 0,
          extra_sum: 0,
          fact_eval_sum: 0,
          acceptance_date: null,
          prepaid: false,
          total_base_curr: '',
          invoice_sums_difference: 0,
          notes: '',
          seller: null,
          date_due: null,
          status: '',
          created: '',
          created_by: ''
        },
        rate: 0,
        rateDate: '',
        acceptancesPopoverShow: false,
        selectedCompletedAcceptances: [],
        selectedBoundedAcceptances: [],
        selectedInvoiceItems: [],
        selectedPackages: [],
        gridOptions: {
          rowSelection: 'multiple',
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            valueFormatter(params) {
              return formatNumber(params.value);
            },
            cellStyle(params) {
              return alignCells(params);
            },
          },
          onRowDataChanged() {
            if (this.rowData && this.rowData.length) {
              autoResize(this);
            } else {
              this.api && this.api.hideOverlay();
              this.api && this.api.showNoRowsOverlay();
            }
          },
          ...gridProps
        },
        acceptancesGrid: {},
        additionalCostsGrid: {},
        commissionFeesGrid: {},
        paymentsGrid: {},
        actualEvaluationGrid: {},
        actualEvaluationColumns: [],
        acceptancesGridColumns: [],
        additionalCostsGridColumns: [],
        commissionFeesGridColumns: [],
        paymentsGridColumns: [
          {'field': 'document_nr', 'headerName': 'document', 'cellRenderer': 'PaymentLink'},
          {'field': 'document_date', 'headerName': 'document_date'},
          {'field': 'payment_date', 'headerName': 'payment_date'},
          {'field': 'payment_sum_str', 'headerName': 'payment_sum'},
          {
            'headerName': 'document_sum', 'children': [
              {'field': 'document_sum_in_invoice_currency_str', 'headerName': 'official_abbr'},
              {'field': 'document_additional_sum_str', 'headerName': 'additional_abbr'}
            ]
          },
          {
            'headerName': 'rate', 'children': [
              {'field': 'currencies_str', 'headerName': '', 'minWidth': 85},
              {'field': 'currency_rate', 'headerName': '', 'minWidth': 85}
            ]
          },
          {
            'headerName': 'document_sum_base_curr', 'children': [
              {'field': 'document_sum_in_base_curr_str', 'headerName': 'official_abbr', 'width': 80},
              {'field': 'additional_sum_base_curr', 'headerName': 'additional_abbr', 'width': 80}
            ]
          },
          {'field': 'rate_difference_in_base_curr_str', 'headerName': 'currency_rate_diff', 'width': 125}
        ],
        pinnedAcceptancesRowData: [],
        pinnedAdditionalCostsRowData: [],
        pinnedCommissionFeesRowData: [],
        acceptances: [],
        acceptancesBoundedWithInvoice: [],
        factEvaluationData: [],
        factEvaluationTotalData: [],
        factEvaluationGridName: '',
        acceptanceBoundedWithInvoices: null,
        additionalCosts: [],
        commissionFees: [],
        payments: [],
        purchaseContracts: [],
        completedOperations: [],
        comments: [],
        documents: [],
        currentTabIndex: 0
      }
    },
    methods: {
      translateColumns,
      async updateTotalData(event, name) {
        const mapper = {
          'actual': {
            url: `accounting/purchase_invoices/${this.invoiceId}/packets/`,
            totalName: 'factEvaluationTotalData'
          },
          'additional': {
            url: `${this.commonUrl}acceptance_additional_costs/`,
            totalName: 'pinnedAdditionalCostsRowData'
          }
        }
        await this.updateTotalDataWith(event, async (params) => {
          const { data: { total } } = await this.$http.get(mapper[name].url, { params });
          this.$set(this, mapper[name].totalName, getPinnedRowData(total));
        })
      },
      prepareSavedData() {
        return {
          invoice_number: this.invoiceData.invoice_number,
          supplier: this.invoiceData.supplier && this.invoiceData.supplier.id,
          currency: this.invoiceData.currency && this.invoiceData.currency.id,
          contract: this.invoiceData.contract && this.invoiceData.contract.id,
          invoice_date: this.invoiceData.invoice_date && moment(this.invoiceData.invoice_date).format(DATE_FORMAT),
          buyer: this.invoiceData.buyer && this.invoiceData.buyer.id,
          total: this.invoiceData.total,
          invoice_sums_difference: this.invoiceData.invoice_sums_difference,
          acceptance_date: this.invoiceData.acceptance_date && moment(this.invoiceData.acceptance_date).format(DATE_FORMAT),
          prepaid: this.invoiceData.prepaid,
          total_base_curr: this.invoiceData.total_base_curr,
          notes: this.invoiceData.notes,
          seller: this.invoiceData.seller && this.invoiceData.seller.id,
          date_due: this.invoiceData.date_due && moment(this.invoiceData.date_due).format(DATE_FORMAT),
          percent_difference: this.invoiceData.percent_difference
        }
      },
      save() {
        const payload = this.prepareSavedData();
        if (this.isNewInvoice) {
          if (this.$route.params.movement_id) {
            payload.acceptances = this.$route.params.movement_id.split(',').map(item => Number(item));
          }
          this.$http.post(`${PURCHASE_INVOICES}`, payload).then(handlerResponse).then(async response => {
            this.invoiceData = {...response};
            this.$notify(setNotifyData(this.$i18n.t(`notifications.common.created`),
              this.$i18n.t(`common_content.purchase_invoice`), 'success'));
            await this.$router.push({name: 'Purchase Invoice', params: {id: response.id}});
            await this.fetchCompletedAcceptances();
            await this.fetchPayments();
            await this.fetchCommissionFees();
            await this.fetchBoundedAcceptances();
            await this.fetchAdditionalCosts();
            await this.fetchActualEvaluation();
          }).catch(error => notificationsRenderer(error.body, 'error'));
        } else {
          this.$http.put(`${this.commonUrl}`, payload).then(handlerResponse).then(async response => {
            this.invoiceData = {...response};
            await this.fetchCompletedAcceptances();
            await this.fetchBoundedAcceptances();
            await this.fetchCommissionFees();
            await this.fetchPayments();
            await this.fetchAdditionalCosts();
            await this.fetchActualEvaluation();
            this.$notify(setNotifyData(this.$i18n.t(`notifications.common.updated`),
              this.$i18n.t(`common_content.purchase_invoice`), 'success'));
          }).catch(error => notificationsRenderer(error.body, 'error'));
        }
      },
      changeInvoiceStatus(statusAction) {
        this.$http.post(`${this.commonUrl}${statusAction}/`).then(handlerResponse).then(response => {
          this.invoiceData = {...response};
          if (statusAction === 'move_status_forward') {
            this.fetchCommissionFees();
          }
          this.currentTabIndex = 0;
        }).catch(error => {
          notificationsRenderer(error.body, 'error');
          this.currentTabIndex = 0;
        });
      },
      acceptancesSelectionChanged() {
        this.selectedBoundedAcceptances = getFilteredSelectedNodes(this.acceptancesGrid);
      },
      actualEvaluationSelectionChanged() {
        const selectedRows = getFilteredSelectedNodes(this.actualEvaluationGrid);
        const groups = selectedRows.map(node => node.parent);
        this.selectedInvoiceItems = _.uniqBy(groups, 'id').filter(data => data.key); // group without key is empty invoice item
        this.selectedPackages = selectedRows.filter(item => item.parent);
      },
      bindAcceptancesToInvoice() {
        if (this.selectedCompletedAcceptances.length) {
          const ids = this.selectedCompletedAcceptances.map(item => item.id);
          this.$http.post(`${this.commonUrl}acceptances/`, {ids}).then(handlerResponse).then(async () => {
            this.getInvoiceInfo();
            await this.fetchBoundedAcceptances();
            this.selectedBoundedAcceptances = [];
            this.selectedCompletedAcceptances = [];
            this.fetchAdditionalCosts();
            this.fetchActualEvaluation();
          }).catch(error => notificationsRenderer(error.body, 'error'))
        } else {
          this.$notify(setNotifyData(this.$i18n.t(`notifications.operations.select_acceptances`), '', 'warn'));
        }
        this.toggleAcceptancesPopover();
      },
      unbindAcceptancesFromInvoice() {
        if (this.selectedBoundedAcceptances.length) {
          const body = {ids: this.selectedBoundedAcceptances.map(item => item.data.id)};
          this.$http.delete(`${this.commonUrl}acceptances/`, {body}).then(handlerResponse).then(async () => {
            this.getInvoiceInfo();
            this.fetchCompletedAcceptances();
            await this.fetchBoundedAcceptances();
            this.selectedBoundedAcceptances = [];
            this.fetchAdditionalCosts();
            this.fetchActualEvaluation();
          }).catch(error => notificationsRenderer(error.body, 'error'))
        } else {
          this.$notify(setNotifyData(this.$i18n.t(`notifications.operations.select_acceptances`), '', 'warn'));
        }
      },
      changeBounedAcceptanceWithInvoices() {
        const filterComponent = this.actualEvaluationGrid.api.getFilterInstance('movement_name');
        filterComponent.selectNothing();
        let model = [];
        if (this.acceptanceBoundedWithInvoices) {
          model = [this.acceptanceBoundedWithInvoices.desc];
        } else {
           model = filterComponent.model.allUniqueValues;
        }
        filterComponent.setModel(model);
        this.actualEvaluationGrid.api.onFilterChanged();
      },
      createActualEvalReport() {
        exportExcel(`accounting/purchase_invoices/${this.invoiceId}/actual_evaluation_report/`);
      },
      async unBindPackagesFromItem() {
        const selectedNodes = getFilteredSelectedNodes(this.actualEvaluationGrid).map(item => item.data);
        const packets = selectedNodes.map(child => child.id);
        const payload = {
          packets
        };
        this.$http.delete(actualEvalBaseUrl, {body: payload}).then(async () => {
          await this.getInvoiceInfo();
          await this.fetchBoundedAcceptances();
          await this.fetchActualEvaluation();
        }).catch(error => notificationsRenderer(error.body, 'error'));
      },
      createActEvaluatForInvoiceItem() {
        const packagesRows = getFilteredSelectedNodes(this.actualEvaluationGrid)
          .filter(item => !item.childrenAfterGroup);
        if (packagesRows.length) {
          const relatedPackages = packagesRows.map(item => item.data.id);
          this.$root.$emit('openActualEvaluationModal', {relatedPackages});
        } else {
          this.$notify(setNotifyData(this.$i18n.t('notifications.actual_evaluation.select_packages_to_create_item'), '', 'warn'));
        }
      },
      /**
       * for input object check if at least one field returns actual value
       * @param {object} object - data
       */
      isAtLeastOneFieldHaveValueOf (object = {}) {
        return Object.entries(object).some(([key, value]) => {
          return Boolean(value) && !['defect_measurements'].includes(key)
        })
      },
      /**
       * handler for defects and additional prices
       * @param {object} formData - current data object
       * @param {function} handler - handler for made up request
       */
      handleAdd (formData, handler) {
        const packagesRows = getFilteredSelectedNodes(this.actualEvaluationGrid)
            .filter(item => !item.childrenAfterGroup)
        if (packagesRows.length) {
          const ids = packagesRows.map(item => item.data.id)

          if (!this.isAtLeastOneFieldHaveValueOf(formData)) {
            this.$notify(setNotifyData(this.$i18n.t('notifications.actual_evaluation.please_fill_fields'), '', 'warn'));
          } else {
            handler(ids, formData)
          }
        } else {
          this.$notify(setNotifyData(this.$i18n.t('notifications.actual_evaluation.please_select_packages'), '', 'warn'));
        }
      },
      addAdditionalPriceToPackets(additionalPriceData) {
        this.handleAdd(additionalPriceData, async (packets, formData) => {
          const { price_difference, proc_addit_services: supplier_additional_perc, close } = formData
          try {
            await this.$http.put('woodware/movement_data/additional_price/', {
              packets,
              price_difference,
              supplier_additional_perc
            })
            await this.getInvoiceInfo()
            await this.fetchCompletedAcceptances()
            await this.fetchBoundedAcceptances()
            await this.fetchAdditionalCosts()
            await this.fetchActualEvaluation()
            close()
          } catch (error) {
            notificationsRenderer(error.body, 'error');
          }
        })
      },
      addDefectToPackets (defectData) {
        this.handleAdd(defectData, async (packets, formData) => {
          const {
            defect_price: penalty,
            defect_measurement: penalty_measure,
            defect_value: penalty_value_nominal,
            close
          } = formData
          try {
            await this.$http.put('woodware/movement_data/penalty/', {
              packets,
              penalty_value_nominal,
              penalty_measure,
              penalty
            })
            await this.getInvoiceInfo()
            await this.fetchCompletedAcceptances()
            await this.fetchBoundedAcceptances()
            await this.fetchAdditionalCosts()
            await this.fetchActualEvaluation()
            close()
          } catch (error) {
            notificationsRenderer(error.body, 'error');
          }
        })
      },
      async editActualEvaluation(row) {
        try {
          const params = {};
          let modalData = {};
          let relatedPackages;
          if (row.data) {
            const { id } = row.data;
            modalData.isPackage = true;
            relatedPackages = [id];
          } else {
            if (row.node.key) {
              relatedPackages = row.node.allLeafChildren.map(node => node.data.id);
            } else {
              return;
            }
          }
          params.id = relatedPackages.toString();
          const response = await this.$http.get(actualEvalBaseUrl, {params}).then(handlerResponse);
          modalData = {
            ...modalData,
            ...response,
            penalty_value_nominal: response.penalty_perc,
            relatedPackages
          };
          this.$root.$emit('openActualEvaluationModal', {...modalData});
        } catch(error) {
          notificationsRenderer(error.body, 'error');
        }
      },
      toggleAcceptancesPopover() {
        this.acceptancesPopoverShow = !this.acceptancesPopoverShow;
      },
      hiddenAcceptancesPopover() {
        this.fetchCompletedAcceptances();
      },
      hiddenAdditionalPricePopover() {
        this.fetchActualEvaluation();
      },
      hiddenDefectPopover() {
        this.fetchActualEvaluation();
      },
      convertSumToBaseCurr() {
        if (this.rate) {
          if (!isNaN(Number(this.invoiceData.total))) {
            this.invoiceData.total_base_curr = (this.invoiceData.total / this.rate).toFixed(2);
          } else {
            this.invoiceData.total_base_curr = '';
          }
        } else {
          if (!isNaN(Number(this.invoiceData.total))) {
            this.invoiceData.total_base_curr = this.invoiceData.total;
          } else {
            this.invoiceData.total_base_curr = '';
          }
        }
      },
      handlePaymentsTabClick (grid, data) {
        this.fetchPayments()
        this.setGridData(grid, data)
      },
      setGridData(grid, data) {
        grid.api.setRowData(data);
      },
      async fetchCurrencyRate() {
        if (this.invoiceData.invoice_date && this.invoiceData.currency) {
          try {
            const params = {
              date: moment(this.invoiceData.invoice_date).format(DATE_FORMAT),
              dest_curr: this.invoiceData.currency.code
            };
            const response = await this.$http.get(`woodware/list/base_currency_rate/`, {params}).then(handlerResponse);
            this.rate = response.rate;
            this.rateDate = response.date;
            this.convertSumToBaseCurr();
          } catch (e) {
            console.log(e);
          }
        } else {
          this.rate = 0;
        }
      },
      async fetchPurchaseContracts() {
        try {
          this.purchaseContracts = await this.$http.get(`woodware/list/purchase_contracts/`).then(handlerResponse);
        } catch (e) {
          console.log(e);
        }
      },
      async fetchCompletedAcceptances(statuses = 'P') {
        const params = {
          processes: 1,
          statuses,
          purchase_invoice__isnull: 'True'
        };
        try {
          this.completedOperations = await this.$http.get(`woodware/list/operations/description/`, {params}).then(handlerResponse);
        } catch (e) {
          console.log(e);
        }
      },
      async getInvoiceInfo() {
        const response = await this.$http.get(`${this.commonUrl}`).then(handlerResponse);
        this.invoiceData = {...response};
        await this.$store.dispatch('location/setPathName', response.invoice_number);
        this.isLoadedData = true;
      },
      async fetchBoundedAcceptances() {
        this.acceptancesGrid.api && this.acceptancesGrid.api.showLoadingOverlay();
        try {
          const response = await this.$http.get(`${this.commonUrl}acceptances/`).then(handlerResponse);
          this.acceptances = response.data;
          this.acceptancesBoundedWithInvoice = response.data.map(({id, name: desc}) => ({desc, id}));
          this.acceptancesGridColumns = addColIdToColumns(response.used_columns);
          this.pinnedAcceptancesRowData = getPinnedRowData(response.total);
        } catch (e) {
            console.log(e)
        } finally {
            autoResize(this.acceptancesGrid);
            this.acceptancesGrid.api && this.acceptancesGrid.api.hideOverlay();
        }
      },
      async saveEvaluationCb(){
        await this.getInvoiceInfo();
        await this.fetchBoundedAcceptances();
        await this.fetchActualEvaluation();
      },
      async fetchAdditionalCosts() {
        try {
          const response = await this.$http.get(`${this.commonUrl}acceptance_additional_costs/`).then(handlerResponse);
          this.additionalCosts = response.data;
          this.additionalCostsGridColumns = addColIdToColumns(response.used_columns);
          this.pinnedAdditionalCostsRowData = getPinnedRowData(response.total);
        } catch (e) {
          console.log(e);
        }
      },
      async fetchCommissionFees() {
        try {
          const response = await this.$http.get(`accounting/purchase_invoices/${this.invoiceId}/agent_fees/`).then(handlerResponse);
          this.commissionFees = response.data;
          this.commissionFeesGridColumns = addColIdToColumns(response.used_columns);
          this.pinnedCommissionFeesRowData = getPinnedRowData(response.total);
        } catch (e) {
          console.log(e);
        }
      },
      async saveCommissionFeeCb() {
        await this.fetchCommissionFees();
      },
      async editCommissionFees(params) {
        if (params) {
          this.$root.$emit('openPurchaseAgentsInvoiceCommissionFeeModal', params.data);
        } else {
          this.$root.$emit('openPurchaseAgentsInvoiceCommissionFeeModal');
        }
      },
      async deleteCommissionFees() {
        const ids = getArrayByProperty(this.commissionFeesGrid.api.getSelectedRows(), 'id');
        if (ids.length) {
          this.$http.delete(`accounting/purchase_agent_commission_fees/`, {body: {ids}}).then((response) => {
            this.$notify(setNotifyData('Success', response.body, 'success'));
            this.fetchCommissionFees();
          }).catch((error) => {
            notificationsRenderer(error.data, 'error');
          });
        } else {
          this.$notify(setNotifyData('Select commission fees for removing', '', 'warn'));
        }
      },
      async fetchPayments() {
        try {
          const response = await this.$http.get(`${this.commonUrl}payments/`).then(handlerResponse);
          this.payments = response.map(item => {
            if (item.row_type === 'difference') {
              item.additional_sum_base_curr = item.difference_additional_sum_str;
            } else {
              item.additional_sum_base_curr = item.document_additional_sum_str;
            }
            return item;
          })
        } catch (e) {
          console.log(e);
        }
      },
      async fetchActualEvaluation() {
        if (this.actualEvaluationGrid.api) {
          this.actualEvaluationGrid.api.showLoadingOverlay()
          this.actualEvaluationGrid.api.addAggFunc('sumAndRound', this.sumAndRound)
        }
        try {
          const response = await this.$http.get(`accounting/purchase_invoices/${this.invoiceId}/packets/`).then(handlerResponse);
          const {data, used_columns, total, grid_name} = response;
          this.factEvaluationGridName = grid_name || '';
          this.$set(this, 'actualEvaluationColumns', [
            {
              hide: true,
              field: this.priceUsageIsPriceList ? 'price_unique_name' : 'poi_unique_name',
              headerName: 'invoice_item',
              rowGroupIndex: -1
            },
            ...used_columns
          ])
          this.factEvaluationData = addGroupingInResponseData(data);
          this.factEvaluationTotalData = convertTotalDataToObject(total);
          this.translateColumns.call(this, this.actualEvaluationColumns)
        } finally {
          this.actualEvaluationGrid.api && this.actualEvaluationGrid.api.hideOverlay();
          this.actualEvaluationGrid.api?.refreshCells()
        }
      },
      async fetchComments() {
        try {
          this.comments = await this.$http.get(`${this.commonUrl}comments/`).then(handlerResponse);
        } catch (e) {
          console.log(e);
        }
      },
      async fetchDocuments() {
        try {
          this.documents = await this.$http.get(`${this.commonUrl}attachments/`).then(handlerResponse);
        } catch (e) {
          console.log(e);
        }
      },
      openHistoryModal() {
        this.$root.$emit('openHistoryModal', this.historySettings);
      },
      sumAndRound (values) {
        const sumOfM3 = Number(values.reduce((acc, value) => {
          acc += value
          return acc
        }, 0))
        return rightRound(sumOfM3, 2).toString().replace('.', ',')
      },
      gridsInitialize() {
        this.acceptancesGrid = {
          ..._.cloneDeep(this.gridOptions),
          components: {
            'OperationLink': LinkCellRenderer('id', '/operation/'),
            'SupplierLink': LinkCellRenderer('distributor_id', '/partners/'),
          },
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            valueFormatter(params) {
              const minDigits = params.colDef.field === 'stock_m3' ? 3 : 2;
              return formatNumber(params.value, minDigits);
            },
            cellStyle(params) {
              return alignCells(params);
            }
          }
        };
        const roundPinned = () => {
          const lotData = {}
          // first we collect all the grouped m3's
          this.actualEvaluationGrid.api.forEachNodeAfterFilterAndSort((node) => {
            const { data } = node
            if (data) {
              if (!lotData[data.poi_unique_name]) {
                lotData[data.poi_unique_name] = 0
              }
              lotData[data.poi_unique_name] += Number(data.nominal_amount)
            }
          })
          // then we summarize rounded sums of m3's and round this with precision 2 also
          // and prepare the answer
          return rightRound(Object.entries(lotData).reduce((acc, [key, value]) => {
            acc += rightRound(value, 2)
            return acc
          }, 0), 2).toString().replace('.', ',')
        }
        this.actualEvaluationGrid = {
          ..._.cloneDeep(this.gridOptions),
          groupSelectsChildren: true,
          suppressAggFuncInHeader: true,
          autoGroupColumnDef: {
            headerName: 'Invoice item',
            checkboxSelection: true,
            width: 260
          },
          components: {
            'OperationLink': LinkCellRenderer('movement_id', '/operation/'),
            'rightRound': roundPinned,
          },
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            valueFormatter(params) {
                if (params.colDef.field === 'penalty_perc') {
                    return formatNumber(params.value, 3);
                } else if (params.colDef.field !== 'pieces' &&
                    params.colDef.field !== 'thickness_nominal' &&
                    params.colDef.field !== 'width_nominal' &&
                    params.colDef.field !== 'length_nominal'){
                    return formatNumber(params.value, 2);
                }
            },
            cellStyle(params) {
                return alignCells(params);
            },
          },
          getRowStyle(params) {
            if (!params.data) {
                if (!params.node.groupData['ag-Grid-AutoColumn']) {
                    return {'font-weight': 'bold', 'background': '#FCCEC4'}
                } else {
                    return {'font-weight': 'bold'}
                }
            } else if (params.data && (!params.data.poi_unique_name && !params.data.price_unique_name) && !params.node.rowPinned) {
                return {'background': '#FCCEC4'}
            }
          }
        }
        this.additionalCostsGrid = {
          ..._.cloneDeep(this.gridOptions),
          components: {
            'OperationLink': LinkCellRenderer('acceptance_id', '/operation/'),
            'PartnerLink': LinkCellRenderer('partner_id', '/partners/'),
          }
        };
        this.commissionFeesGrid = {
          ..._.cloneDeep(this.gridOptions),
          components: {
            'AgentLink': LinkCellRenderer('agent_id', '/partners/'),
          },
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            valueFormatter(params) {
              return formatNumber(params.value);
            },
            cellStyle(params) {
              return alignCells(params);
            }
          }
        },
        this.paymentsGrid = {
          ..._.cloneDeep(this.gridOptions),
          components: {
            'PaymentLink': LinkCellRenderer('payment_id', '/payments/')
          },
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            valueFormatter(params) {
              return formatNumber(params.value, 5);
            },
            cellStyle(params) {
              return alignCells(params);
            }
          },
          getRowStyle(params) {
            if (params.data.row_type === 'invoice') {
              return {'border-top': '2px solid #000', 'border-bottom': '2px solid #000', 'font-weight': 'bold'};
            }
            if (params.data.row_type === 'total') {
              return {'border-top': '2px solid #000', 'border-bottom': '1px solid #000', 'font-weight': 'bold'};
            }
            if (params.data.row_type === 'difference') {
              return {'border-top': '1px solid #000', 'border-bottom': '2px solid #000', 'font-weight': 'bold'};
            }
          }
        }
      }
    },
    computed: {
      ...mapState('generic', ['currencies', 'currentRoutePermissions']),
      ...mapGetters('login', ['isSuperuserOrAppraiser']),
      ...mapState('woodware', ['base_currency']),
      ...mapGetters('woodware', ['sellers', 'suppliers', 'buyers']),
      invoiceId() {
        return this.$route.params.id;
      },
      isNewInvoice() {
        return this.invoiceId === 'new';
      },
      invoiceLabel() {
        return this.isNewInvoice ? `${this.$i18n.t('common_content.new')} ${this.$i18n.t('common_content.invoice')}` :
          `${this.$i18n.t('common_content.invoice')} / ${this.invoiceData.invoice_number} - ${this.invoiceData.status}`;
      },
      invoiceInfo() {
        return this.isNewInvoice ? '' :
          `${this.invoiceData.created} / ${this.$i18n.t('common_content.creator')}: ${this.invoiceData.created_by}`;
      },
      priceUsageIsPriceList () {
        return this.invoiceData.supplier?.price_usage === 'price_list'
      },
      historySettings() {
        return {url: `purchase_invoice/?id=${this.invoiceId}`, title: this.invoiceLabel.split('/')[1]}
      },
      commonUrl() {
        return `${PURCHASE_INVOICES}${this.invoiceId}/`;
      },
      isApprovedInvoice() {
        return this.invoiceData.status === 'approved';
      },
      isDraft() {
        return this.invoiceData.status === 'draft';
      },
      gridHeight() {
        if (window.innerHeight > 1024) {
          return this.showCollapse ? {height: 'calc(100vh - 565px)'} : {height: 'calc(100vh - 265px)'};
        } else {
          return this.showCollapse ? {height: '400px'} : {height: 'calc(100vh - 265px)'};
        }
      },
      invoiceDataForCommissionFeeModal() {
        return {
          actual_cost: this.invoiceData.actual_cost,
          actual_evaluation_cost: this.invoiceData.actual_evaluation_cost,
          nom_lm: this.invoiceData.nom_lm,
          nom_m2: this.invoiceData.nom_m2,
          nom_m3: this.invoiceData.nom_m3,
          nom_pcs: this.invoiceData.nom_pcs
        }
      },
      isUserCanViewCommissionFees() {
        return this.currentRoutePermissions.view_purchase_agent_fees;
      }
    },
    async beforeMount() {
      await this.$store.cache.dispatch('woodware/getUsers');
      await this.$store.cache.dispatch('generic/getCurrencies');
      await this.$store.dispatch('woodware/getPartners', {is_active: true});
      await this.$store.cache.dispatch('woodware/getUsers');
      await this.fetchPurchaseContracts();
      this.invoiceData.currency = this.currencies.find(item => item.code === `${this.base_currency.code}`);
      this.gridsInitialize();
      if (this.isNewInvoice) {
        this.isLoadedData = true;
        const routeParams = this.$route.params;
        this.invoiceData.acceptance_date = routeParams.receiving_date;
        this.invoiceData.contract = this.purchaseContracts.find(item => item.contract_nr === routeParams.contract);
        this.invoiceData.supplier = this.suppliers.find(item => item.name === routeParams.supplier);
      } else {
        translateColumns.call(this, this.paymentsGridColumns);
        await this.fetchCompletedAcceptances();
        await this.fetchAdditionalCosts();
        await this.fetchCommissionFees();
        await this.fetchPayments();
        await this.getInvoiceInfo();
        await this.fetchBoundedAcceptances();
        await this.fetchActualEvaluation();
        await this.fetchComments();
        await this.fetchDocuments();
        await this.fetchCurrencyRate();
        this.convertSumToBaseCurr();
      }
      console.log(this.factEvaluationTotalData)
    }
  }
</script>

<style scoped lang="scss">
  .filter-form {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-gap: 5px 15px;
    margin-top: 10px;
  }

  .checkbox-wrapper {
    display: block;
    padding-top: 27px;
  }

  .rate {
    font-size: 11px;
  }

  .actions-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .grid {
    height: 450px;
    margin-top: 9px;
  }

  .load-icon {
    margin-left: 5px;
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/modules/purchases/invoices/PurchaseInvoice.vue